import React, {FC, useMemo} from 'react';

import {Trans} from '../../../../../../libs/shared/core/src/helpers/internationalization/react';
import {CellsContext} from '../../../../../../libs/shared/core/src/store/panels/cells/context/cellsContext';
import {TimelineVariationsEnum} from '../../../../../../libs/shared/core/src/store/timelineVariations/timelineVariationsTypes';
import {useConversationStatusActionOfflinePermission} from '../../../../../../srcCommon/permissions/useOfflinePermission';
import {useAppSelector} from '../../../../../helpers/appReduxHelpers';
import {ConversationModel} from '../../../../../models/api/conversationModel';
import {DefaultSendActionsEnum} from '../../../../../models/api/preferencesModel';
import {makeGetIsSendActionTicketingEnabled} from '../../../../../store/data/conversations/conversationsMultiSelectors';
import {SendTicketStatusToolbarButton} from './sendTicketStatusToolbarButton';
import {SendMessage, SendToolbarButton} from './sendToolbarButton';

/*
 * Props.
 */

export interface SendToolbarButtonContainerProps {
  cellsContext: CellsContext;
  conversation: ConversationModel | null;
  // TODO: Update upstream types so we just get a `channelId`.
  channel: {id: number};
  hasValidationErrors: boolean;
  areSendShortcutsDisabled?: boolean;
  hasClosePermission: boolean;
  hasSendPermission: boolean;
  defaultSendAction: DefaultSendActionsEnum;
  sendMessage: SendMessage;
  hasTrialExpired?: boolean;
  hasTeamTrialExpired?: boolean;
  timelineVariation: TimelineVariationsEnum;
  hasArchivePermission: boolean;
}

/*
 * Component.
 */

export const SendToolbarButtonContainer: FC<SendToolbarButtonContainerProps> = (props) => {
  // Check whether we're allowed to archive/snooze the conversation when we send.
  const updateConversationStatusOfflinePermission = useConversationStatusActionOfflinePermission(
    props.cellsContext,
    props.conversation ? [props.conversation] : [],
  );

  const isLabelHidden = props.timelineVariation === TimelineVariationsEnum.SYNCHRONOUS;

  // If we're not allowed to archive/snooze, make the send button the default.
  const allowedDefaultSendAction =
    updateConversationStatusOfflinePermission.isAllowed && props.hasArchivePermission
      ? props.defaultSendAction
      : DefaultSendActionsEnum.SEND;

  // Check if trial has expired to disable sending
  const canUserSend = props.hasSendPermission && !props.hasTrialExpired && !props.hasTeamTrialExpired;

  const isDisabled = !canUserSend || props.hasValidationErrors;

  // If the button is disabled, add a tooltip to explain why.
  const renderTooltip = () => {
    if (canUserSend) {
      return null;
    }

    return <Trans id="composer-send-nopermission">You do not have permission to send this message</Trans>;
  };

  const getIsSendActionTicketingEnabled = useMemo(
    () =>
      makeGetIsSendActionTicketingEnabled<SendToolbarButtonContainerProps>(
        (_state, params) => params.channel.id,
        (_state, params) => params.conversation ?? undefined,
      ),
    [],
  );
  const isSendActionTicketingEnabled = useAppSelector((state) =>
    getIsSendActionTicketingEnabled(state, props),
  );

  if (isSendActionTicketingEnabled) {
    return (
      <SendTicketStatusToolbarButton
        conversation={props.conversation}
        isDisabled={isDisabled}
        renderTooltip={renderTooltip}
        hasClosePermission={props.hasClosePermission}
        updateConversationStatusOfflinePermission={updateConversationStatusOfflinePermission}
        areSendShortcutsDisabled={props.areSendShortcutsDisabled}
        onSendClick={props.sendMessage}
      />
    );
  }

  return (
    <SendToolbarButton
      isDisabled={isDisabled}
      renderTooltip={renderTooltip}
      areSendShortcutsDisabled={props.areSendShortcutsDisabled}
      hasClosePermission={props.hasClosePermission}
      updateConversationStatusOfflinePermission={updateConversationStatusOfflinePermission}
      defaultSendAction={allowedDefaultSendAction}
      sendMessage={props.sendMessage}
      conversation={props.conversation}
      isLabelHidden={isLabelHidden}
    />
  );
};
